import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, Alert, CircularProgress, useTheme, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useHistory, useRouteMatch, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import InvItemAutocomplete from '../../components/InvItemAutocomplete';
import { createRequisition, updateRequisition, submitRequisition,
  getRequisition, getRequisitionItems } from '../../api/dispensing';
import { getStores, getStockByStoreItem, readAvailableQuantity } from '../../api/stockLedger';
import { getUoms } from '../../api/inventoryMaster';
import { isRootStore, isRootStoreUser } from '../../utils/storeEntity';
import { filterStoresByAssignment } from '../../utils/assignedStores';
import { resolveLineId, toNumberOrNull, resolveDispensingScope } from '../../utils/dispensingEntity';
import { normalizeUomList } from '../../utils/masterEntity';
import { getStoreActorId } from '../../hooks/useStoreScope';
import { PRIORITY_LEVELS } from '../../constants';
import { generateTxnNumber } from '../../utils';
import { useEmrContext } from '../../hooks/useEmrContext';
import useInvRole from '../../hooks/useInvRole';
import { IPD_COLORS } from '../../theme';

const LINE_DEFAULT = {
  item_id: '',
  uom_id: '',
  uom_name: '',
  requested_quantity: '',
  unit_rate: '',
  remarks: '',
  own_stock: null,
  stock_loading: false,
};

function buildUomMap(uoms) {
  const map = {};
  (Array.isArray(uoms) ? uoms : []).forEach((u) => {
    const id = u.uom_id || u.id;
    if (id) map[id] = u.uom_name || u.uom_code || u.name || '';
  });
  return map;
}

function resolveUomName(line, uomMap = {}) {
  if (line?.uom_name) return line.uom_name;
  if (line?.uom_id && uomMap[line.uom_id]) return uomMap[line.uom_id];
  return '';
}

function lineFields(line) {
  return {
    item_id: line.item_id,
    uom_id: line.uom_id || undefined,
    requested_quantity: Number(line.requested_quantity),
    unit_rate: line.unit_rate !== '' && line.unit_rate != null ? Number(line.unit_rate) : undefined,
    remarks: line.remarks || undefined,
  };
}

function isLineDirty(line) {
  const original = line._original;
  if (!original) return true;
  return (
    String(line.item_id) !== String(original.item_id) ||
    String(line.uom_id ?? '') !== String(original.uom_id ?? '') ||
    toNumberOrNull(line.requested_quantity) !== toNumberOrNull(original.requested_quantity) ||
    toNumberOrNull(line.unit_rate) !== toNumberOrNull(original.unit_rate) ||
    (line.remarks || '') !== (original.remarks || '')
  );
}

function buildEditItemsPayload(currentLines, originalServerIds) {
  const validLines = currentLines.filter(l => l.item_id && Number(l.requested_quantity) > 0);
  const add_items = [];
  const update_items = [];
  const presentIds = new Set();

  for (const line of validLines) {
    const serverId = line._serverId ?? null;
    if (serverId == null) {
      add_items.push(lineFields(line));
    } else {
      presentIds.add(serverId);
      if (isLineDirty(line)) update_items.push({ id: serverId, ...lineFields(line) });
    }
  }

  const delete_item_ids = originalServerIds.filter(sid => !presentIds.has(sid));
  return { add_items, update_items, delete_item_ids };
}

const ACTION_BAR_SX = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1.5,
  flexWrap: 'wrap',
  width: { xs: '100%', sm: 'auto' },
  '& > button': { flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 'auto' } },
};

function formatStock(qty) {
  if (qty == null) return '0';
  return String(qty);
}

export default function RequisitionForm() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const emr = useEmrContext();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [stores, setStores] = useState([]);
  const [uomMap, setUomMap] = useState({});
  const [lines, setLines] = useState([{ ...LINE_DEFAULT }]);
  const originalServerIdsRef = useRef([]);
  const stockReqRef = useRef(0);

  const stockBase = `${url.split('/stock/')[0]}/stock`;

  const { myStoreId, myStoreIds } = useInvRole();

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      transaction_number: generateTxnNumber('REQ'),
      from_store_id: '',
      to_store_id: '',
      transaction_date: dayjs().format('YYYY-MM-DD'),
      priority: 'NORMAL',
      remarks: '',
    },
  });

  const fromStoreId = watch('from_store_id');

  useEffect(() => {
    getStores().then(d => setStores(Array.isArray(d) ? d : (d?.data || d?.items || []))).catch(() => {});
    getUoms({ is_active: true, limit: 200 })
      .then(d => setUomMap(buildUomMap(normalizeUomList(d))))
      .catch(() => setUomMap({}));
  }, []);

  const isParentStoreUser = isRootStoreUser(myStoreId, stores);

  // Safety net: the two visible entry points (RequisitionList, StockOverview) already
  // hide Create Requisition for Parent-store users — this covers direct URL access.
  // Create mode only; `stores.length` gates it to only fire once stores have actually
  // loaded, so it never fires on the empty initial state (fail-open, same contract as
  // isRootStoreUser itself, and the same "stores.some(...)" membership guard already
  // used by lockFromStore right above this).
  useEffect(() => {
    if (id || !stores.length || !isParentStoreUser) return;
    toast.error('Parent Store users cannot create a requisition.');
    history.replace(`${stockBase}/requisitions`);
  }, [id, stores, isParentStoreUser, history, stockBase]);

  // The requesting store is the user's own; the central store they request FROM
  // stays open. Guarded on membership because fromStoreOptions excludes root stores,
  // so a central-store user's own store is legitimately absent — leave it open then.
  // Create only: the edit path reset()s from the server and must not be clobbered.
  const lockFromStore = !id && myStoreIds.length === 1
    && stores.some(s => !isRootStore(s) && (s.store_id || s.id) === myStoreId);

  useEffect(() => {
    if (lockFromStore) setValue('from_store_id', myStoreId);
  }, [lockFromStore, myStoreId, setValue]);

  useEffect(() => {
    if (!Object.keys(uomMap).length) return;
    setLines(prev => prev.map(row => {
      if (!row.uom_id || row.uom_name) return row;
      const name = uomMap[row.uom_id];
      return name ? { ...row, uom_name: name } : row;
    }));
  }, [uomMap]);

  const fetchOwnStock = useCallback(async (storeId, itemId) => {
    if (!storeId || !itemId) return null;
    try {
      const data = await getStockByStoreItem(storeId, itemId);
      return readAvailableQuantity(data);
    } catch (e) {
      console.error('[RequisitionForm] stock lookup failed:', e);
      return null;
    }
  }, []);

  const refreshOwnStocks = useCallback(async (storeId) => {
    if (!storeId) {
      setLines(prev => prev.map(row => ({ ...row, own_stock: null, stock_loading: false })));
      return;
    }
    const gen = ++stockReqRef.current;
    let snapshot = [];
    setLines(prev => {
      snapshot = prev.map(row => row.item_id || '');
      return prev.map(row => (
        row.item_id ? { ...row, stock_loading: true } : { ...row, own_stock: null, stock_loading: false }
      ));
    });
    const qtys = await Promise.all(
      snapshot.map(itemId => (itemId ? fetchOwnStock(storeId, itemId) : Promise.resolve(null))),
    );
    if (gen !== stockReqRef.current) return;
    setLines(prev => prev.map((row, i) => (
      row.item_id && snapshot[i] === row.item_id
        ? { ...row, own_stock: qtys[i], stock_loading: false }
        : { ...row, stock_loading: false }
    )));
  }, [fetchOwnStock]);

  useEffect(() => {
    refreshOwnStocks(fromStoreId);
  }, [fromStoreId, refreshOwnStocks]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [r, items] = await Promise.all([
          getRequisition(id),
          getRequisitionItems(id).catch(() => []),
        ]);
        if (r?.status && r.status !== 'DRAFT') {
          history.replace(`${stockBase}/requisitions/${id}`);
          return;
        }
        reset({
          transaction_number: r.transaction_number || '',
          from_store_id: r.from_store_id || '',
          to_store_id: r.to_store_id || '',
          transaction_date: r.transaction_date ? dayjs(r.transaction_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
          priority: r.priority || 'NORMAL',
          remarks: r.remarks || '',
        });
        const its = Array.isArray(items) ? items : (items?.items || items?.data || []);
        originalServerIdsRef.current = its.map(resolveLineId).filter(v => v != null);
        const nextLines = its.length ? its.map(it => {
          const uomId = it.uom_id || '';
          const snapshot = {
            item_id: it.item_id || '',
            uom_id: uomId,
            uom_name: it.uom_name || it.uom_code || '',
            requested_quantity: it.requested_quantity ?? '',
            unit_rate: it.unit_rate ?? '',
            remarks: it.remarks || '',
          };
          return {
            ...snapshot,
            own_stock: null,
            stock_loading: Boolean(r.from_store_id && it.item_id),
            _serverId: resolveLineId(it),
            _original: snapshot,
          };
        }) : [{ ...LINE_DEFAULT }];
        setLines(nextLines);
        if (r.from_store_id) {
          const gen = ++stockReqRef.current;
          const qtys = await Promise.all(
            nextLines.map(row => (
              row.item_id ? fetchOwnStock(r.from_store_id, row.item_id) : Promise.resolve(null)
            )),
          );
          if (gen !== stockReqRef.current) return;
          setLines(prev => prev.map((row, i) => ({
            ...row,
            own_stock: qtys[i],
            stock_loading: false,
          })));
        }
      } catch (e) { console.error('[RequisitionForm] load draft failed:', e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function addLine(afterIndex) {
    setLines(l => {
      const newLine = { ...LINE_DEFAULT };
      if (afterIndex === undefined) return [...l, newLine];
      const next = [...l];
      next.splice(afterIndex + 1, 0, newLine);
      return next;
    });
  }

  function removeLine(i) { setLines(l => l.filter((_, idx) => idx !== i)); }

  async function updateLine(i, key, val, item) {
    setLines(l => l.map((row, idx) => {
      if (idx !== i) return row;
      const updated = { ...row, [key]: val };
      if (key === 'item_id') {
        // Prefer issue UOM for stock requests; fall back to base when issue UOM is not set.
        const uomId = item?.default_issue_uom_id || item?.base_uom_id || '';
        updated.uom_id = uomId;
        updated.uom_name =
          item?.default_issue_uom_name
          || item?.base_uom_name
          || item?.uom_name
          || uomMap[uomId]
          || '';
        updated.own_stock = null;
        updated.stock_loading = Boolean(val && fromStoreId);
      }
      return updated;
    }));

    if (key === 'item_id' && val && fromStoreId) {
      const qty = await fetchOwnStock(fromStoreId, val);
      setLines(l => l.map((row, idx) => (
        idx === i && row.item_id === val
          ? { ...row, own_stock: qty, stock_loading: false }
          : row
      )));
    } else if (key === 'item_id' && !val) {
      setLines(l => l.map((row, idx) => (
        idx === i ? { ...row, own_stock: null, stock_loading: false } : row
      )));
    }
  }

  function invalidLineMessage(line) {
    if (!line.item_id) return null;
    if (!(Number(line.requested_quantity) > 0)) {
      return 'One of the item rows has an item selected but no valid quantity. Fix the quantity or remove the row before saving.';
    }
    if (!line.uom_id) {
      return 'One of the item rows is for an item with no issue/base UOM configured, so it cannot be saved. Remove the row or fix the item master.';
    }
    return null;
  }

  async function save(data, submitAfter) {
    setError(null);
    for (const line of lines) {
      const msg = invalidLineMessage(line);
      if (msg) { setError(msg); return; }
    }
    const validLines = lines.filter(l => l.item_id && Number(l.requested_quantity) > 0);
    if (validLines.length === 0) {
      setError('Add at least one item with a valid quantity.');
      return;
    }
    setSaving(true);
    try {
      let reqId = id;
      let savedRow = null;
      if (isEdit) {
        const { add_items, update_items, delete_item_ids } =
          buildEditItemsPayload(lines, originalServerIdsRef.current);
        savedRow = await updateRequisition(id, {
          transaction_date: data.transaction_date,
          priority: data.priority,
          remarks: data.remarks || undefined,
          updated_by: getStoreActorId() || undefined,
          add_items,
          update_items,
          delete_item_ids,
        });
      } else {
        savedRow = await createRequisition({
          ...data,
          ...(emr.department_id ? { department_id: emr.department_id } : {}),
          ...(emr.ward_id ? { ward_id: emr.ward_id } : {}),
          external_admission_id: emr.external_admission_id || undefined,
          items: validLines.map(l => lineFields(l)),
        });
        reqId = savedRow?.transaction_id || savedRow?.id || savedRow?.data?.transaction_id || savedRow?.data?.id;
      }
      if (submitAfter && reqId) {
        const scope = resolveDispensingScope(
          {
            ...(savedRow || {}),
            from_store_id: savedRow?.from_store_id || data.from_store_id,
            to_store_id: savedRow?.to_store_id || data.to_store_id,
          },
          stores,
        );
        await submitRequisition(reqId, scope);
        history.push(`${stockBase}/requisitions`);
      } else if (reqId && !isEdit) {
        history.replace(`${stockBase}/requisition/${reqId}/edit`);
      } else {
        history.push(`${stockBase}/requisitions`);
      }
    } catch (e) {
      console.error('[RequisitionForm] save failed:', e);
      setError(e.message || 'Failed to save the requisition.');
    } finally { setSaving(false); }
  }

  const toStoreOptions = stores
    .filter(s => isRootStore(s))
    .map(s => ({ value: s.store_id || s.id, label: `${s.store_name} (${s.store_code})` }));
  const fromStoreOptions = filterStoresByAssignment(stores, myStoreIds)
    .filter(s => !isRootStore(s))
    .map(s => ({ value: s.store_id || s.id, label: `${s.store_name} (${s.store_code})` }));

  const onPrimaryAction = handleSubmit(d => save(d, isEdit));

  const actionButtons = (
    <>
      <InvButton variant="outlined" tone="teal" onClick={() => history.goBack()}>Cancel</InvButton>
      <InvButton type="button" onClick={onPrimaryAction} loading={saving}>
        {isEdit ? 'Submit for Approval' : 'Submit'}
      </InvButton>
    </>
  );

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); onPrimaryAction(); }}>
      <PageHeader
        eyebrow="Inventory Management"
        title={isEdit ? 'Edit Requisition' : 'New Requisition'}
        description={
          isEdit
            ? 'Draft saved. Review details and submit for approval when ready.'
            : 'Submit saves a draft first so you can review and edit before sending for approval.'
        }
        actions={!isMobile ? <Box sx={ACTION_BAR_SX}>{actionButtons}</Box> : undefined}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {isEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This requisition is in <strong>DRAFT</strong>. You can edit it here. Click <strong>Submit for Approval</strong> when ready.
        </Alert>
      )}
      {!fromStoreId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select <strong>From Store (Requesting)</strong> first — available stock for that store appears after you pick an item.
        </Alert>
      )}
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <SectionCard title="Requisition Details" sx={{ scrollMarginTop: 16 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller name="transaction_number" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => <InvTextField {...field} label="Requisition # *" error={errors.transaction_number} />} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="from_store_id" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvSelect label="From Store (Requesting) *" value={field.value} onChange={field.onChange}
                      options={fromStoreOptions} error={errors.from_store_id} disabled={lockFromStore} />
                  )} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="to_store_id" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvSelect label="To Store (Central) *" value={field.value} onChange={field.onChange}
                      options={toStoreOptions} error={errors.to_store_id} />
                  )} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller name="transaction_date" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => <InvTextField {...field} label="Date *" type="date" InputLabelProps={{ shrink: true }} error={errors.transaction_date} />} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller name="priority" control={control}
                  render={({ field }) => (
                    <InvSelect label="Priority" value={field.value} onChange={field.onChange}
                      options={PRIORITY_LEVELS.map(p => ({ value: p, label: p }))} />
                  )} />
              </Grid>
              {emr.external_admission_id && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ px: 1.5, py: 1, border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block' }}>Admission</Typography>
                    <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 14 }}>{emr.external_admission_id}</Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Controller name="remarks" control={control}
                  render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />} />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Items Requested" sx={{ mt: 2.5 }}
            actions={<InvButton size="small" startIcon={<AddIcon />} onClick={() => addLine()}>Add Row</InvButton>}>
            <Box sx={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
              <Table size="small" sx={{
                minWidth: 720,
                '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 220 }}>Item</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, minWidth: 110 }}>
                      Available Qty
                    </TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Qty *</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>UOM</TableCell>
                    {/* <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Unit Rate</TableCell> */}
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 140 }}>Remarks</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <IconButton size="small" aria-label="Remove row" onClick={() => removeLine(i)}
                          sx={{ color: IPD_COLORS.error.text }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <InvItemAutocomplete
                          value={line.item_id}
                          storeId={fromStoreId}
                          onChange={(itemId, item) => updateLine(i, 'item_id', itemId, item)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {line.stock_loading ? (
                          <CircularProgress size={16} sx={{ color: IPD_COLORS.primary }} />
                        ) : (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: !line.item_id
                                ? IPD_COLORS.textSecondary
                                : ((Number(line.own_stock) || 0) > 0
                                  ? IPD_COLORS.success.text
                                  : IPD_COLORS.warning.text),
                            }}
                            aria-label={`Available stock at requesting store: ${formatStock(line.own_stock)}`}
                          >
                            {line.item_id ? formatStock(line.own_stock) : '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.requested_quantity} label=""
                          onChange={e => updateLine(i, 'requested_quantity', e.target.value)}
                          type="number"
                          inputProps={{ min: 0.01, step: 0.01, 'aria-label': 'Requested quantity' }} />
                      </TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}
                          aria-label={`Unit of measure: ${resolveUomName(line, uomMap) || 'none'}`}
                        >
                          {line.item_id ? (resolveUomName(line, uomMap) || '—') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* <InvTextField value={line.unit_rate} label=""
                          onChange={e => updateLine(i, 'unit_rate', e.target.value)}
                          type="number"
                          inputProps={{ min: 0, 'aria-label': 'Unit rate' }} />
                        */}
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.remarks} label=""
                          onChange={e => updateLine(i, 'remarks', e.target.value)}
                          inputProps={{ 'aria-label': 'Line remarks' }} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" aria-label="Add row below" onClick={() => addLine(i)}
                          sx={{ color: IPD_COLORS.primary }}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {lines.length === 0 && (
              <Typography sx={{ color: IPD_COLORS.textSecondary, textAlign: 'center', py: 3 }}>
                Add at least one item.
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {isMobile && (
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          mt: 3,
          mx: { xs: -2, sm: -3 },
          px: { xs: 2, sm: 3 },
          py: 2,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0px -4px 16px rgba(0,0,0,0.08)',
          ...ACTION_BAR_SX,
        }}>
          {actionButtons}
        </Box>
      )}
    </Box>
  );
}
